import { API_ENDPOINTS } from '../constants';

import { get, post } from './apiClient';

import type { ComponentDraft } from './aiScoringDraftMapper';

export interface AIScoringRunSummary {
  id: string;
  evaluation_id: string;
  status: string;
  engine: string;
  model_name: string | null;
  language?: string | null;
  error_message?: string | null;
  segment_count: number;
  created_at: string;
  completed_at: string | null;
}

export interface TranscriptionTriggerRequest {
  video_upload_id?: number;
  language?: string;
  model_name?: string;
}

export async function triggerTranscription(
  evaluationId: string,
  body: TranscriptionTriggerRequest = {}
): Promise<AIScoringRunSummary> {
  return post<AIScoringRunSummary>(
    API_ENDPOINTS.EVALUATIONS.AI_SCORING.TRIGGER(evaluationId),
    body
  );
}

export async function getAiScoringRun(
  evaluationId: string,
  runId: string
): Promise<AIScoringRunSummary> {
  return get<AIScoringRunSummary>(
    API_ENDPOINTS.EVALUATIONS.AI_SCORING.RUN(evaluationId, runId)
  );
}

export async function listAiScoringRuns(
  evaluationId: string
): Promise<AIScoringRunSummary[]> {
  return get<AIScoringRunSummary[]>(
    API_ENDPOINTS.EVALUATIONS.AI_SCORING.RUNS(evaluationId)
  );
}

export async function listComponentDrafts(
  evaluationId: string,
  runId?: string
): Promise<ComponentDraft[]> {
  const query = runId ? `?run_id=${encodeURIComponent(runId)}` : '';
  return get<ComponentDraft[]>(
    `${API_ENDPOINTS.EVALUATIONS.AI_SCORING.DRAFTS(evaluationId)}${query}`
  );
}

export async function generateComponentDrafts(
  evaluationId: string,
  runId: string
): Promise<ComponentDraft[]> {
  return post<ComponentDraft[]>(
    API_ENDPOINTS.EVALUATIONS.AI_SCORING.GENERATE_DRAFTS(evaluationId, runId)
  );
}

/** Latest completed transcription run, if any. */
export async function getLatestCompletedRun(
  evaluationId: string
): Promise<AIScoringRunSummary | null> {
  const runs = await listAiScoringRuns(evaluationId);
  return runs.find(run => run.status.toLowerCase() === 'completed') ?? null;
}
